import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { createT } from '../utils/simpleTranslate';
import { formatDateForPDF } from '../utils/dataFormatters';
import { yearsFromDate, getHealthIDLabel, getPatientID } from '../../../utils';

/**
 * PDF Patient Info Component
 * 
 * Replaces PatientInfoSection component with @react-pdf/renderer equivalent.
 * Displays patient information in a single column layout.
 */

interface IPatient {
  id?: string;
  personalData?: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    insurance?: string | number;
    healthId?: string;
    automated_health_id?: string;
    nationalId?: string;
    passportNumber?: string;
  };
}

interface PDFPatientInfoProps {
  patient: IPatient;
  locale?: string;
  patientInsurance?: { name: string } | null;
  translations?: {
    name?: string;
    birthdate?: string;
    age?: string;
    sex?: string;
    healthId?: string;
    insurance?: string;
    phone?: string;
  };
}

export const PDFPatientInfo: React.FC<PDFPatientInfoProps> = ({
  patient,
  locale = 'fr',
  patientInsurance,
  translations,
}) => {
  const t = createT(locale);
  
  // Create single column layout taking 50% of page width
  const singleColumnLayout = {
    container: {
      width: '50%',
    },
  };
  
  // Use translations from props or defaults
  const labels = translations || {
    name: t('general.full_name'),
    birthdate: t('investigation.create.personal_data.fields.birthdate'),
    age: t('general.age'),
    sex: t('investigation.create.personal_data.fields.sex'),
    insurance: t('investigation.create.personal_data.fields.insurance'),
    phone: t('investigation.create.personal_data.fields.phone'),
  };
  
  // Get health ID label dynamically based on patient data
  const healthIdLabel = translations?.healthId || t(getHealthIDLabel(patient));

  if (!patient?.personalData) {
    return (
      <Text style={specializedTypography.statusInactive}>
        {t('patient.info_not_available')}
      </Text>
    );
  }

  const { personalData } = patient;

  return (
    <View style={singleColumnLayout.container}>
      {/* Patient Name */}
      <View style={{ marginBottom: 3 }}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {labels.name}:
          </Text>
          <Text style={specializedTypography.patientName}>
            {' '}{personalData.name} {personalData.surnames}
          </Text>
        </Text>
      </View>

      {/* Birthdate */}
      {personalData.birthdate && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.birthdate}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{formatDateForPDF(personalData.birthdate, locale)}
            </Text>
          </Text>
        </View>
      )}

      {/* Sex */}
      {personalData.sex && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.sex}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.sex}
            </Text>
          </Text>
        </View>
      )}

      {/* Health ID */}
      <View style={{ marginBottom: 3 }}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {healthIdLabel}:
          </Text>
          <Text style={specializedTypography.patientId}>
            {' '}{getPatientID(patient)}
          </Text>
        </Text>
      </View>

      {/* Insurance */}
      {patientInsurance && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.insurance}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{patientInsurance.name}
            </Text>
          </Text>
        </View>
      )}

      {/* Phone */}
      {personalData.phone && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.phone}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.phone}
            </Text>
          </Text>
        </View>
      )}

      {/* Age */}
      {personalData.birthdate && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.age}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{yearsFromDate(new Date(personalData.birthdate))} {t('general.years')}
            </Text>
          </Text>
        </View>
      )}
    </View>
  );
};

export default PDFPatientInfo;
